import React from 'react';

import { cn } from './cn';

export interface CircularProgressProps {
  /**
   * Progress value from 0 to 100
   */
  value: number;
  /**
   * Size of the circular progress indicator in pixels
   * @default 24
   */
  size?: number;
  /**
   * Stroke width of the circle
   * @default 2
   */
  strokeWidth?: number;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * A circular progress indicator component that displays progress from 0-100%
 */
export const CircularProgress = React.forwardRef<
  SVGSVGElement,
  CircularProgressProps
>(({ value, size = 24, strokeWidth = 2, className }, ref) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      className={cn('-rotate-90 transform', className)}
      aria-label={`${value}% complete`}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-border-primary"
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="text-bg-accent-strong-primary transition-all duration-300 ease-in-out"
      />
    </svg>
  );
});

CircularProgress.displayName = 'CircularProgress';
